/**
 * ThemeToggle (item #9 Phase 1).
 *
 * Cycles light -> dark -> earth -> system on click.
 * Icon shows current resolved state with a small modifier for 'system'.
 * Tooltip explains current state and what click will do.
 */
import { Leaf, Moon, Monitor, Sun } from 'lucide-react';
import { useTheme, type ThemePreference } from '../../lib/theme.js';
import { Tooltip } from './tooltip.js';
import { cn } from '../../lib/utils.js';

const ORDER: ThemePreference[] = ['light', 'dark', 'earth', 'system'];

const LABEL: Record<ThemePreference, string> = {
  light: 'Light',
  dark: 'Dark',
  earth: 'Earth',
  system: 'System',
};

const NEXT_LABEL: Record<ThemePreference, string> = {
  light: 'Switch to dark',
  dark: 'Switch to earth',
  earth: 'Switch to system',
  system: 'Switch to light',
};

interface ThemeToggleProps {
  className?: string;
  /** Show inline label next to icon */
  showLabel?: boolean;
}

export default function ThemeToggle({ className, showLabel }: ThemeToggleProps) {
  const { preference, setPreference } = useTheme();

  function cycle() {
    const i = ORDER.indexOf(preference);
    const next: ThemePreference = ORDER[(i + 1) % ORDER.length] ?? 'system';
    setPreference(next);
  }

  const Icon = preference === 'light' ? Sun : preference === 'dark' ? Moon : preference === 'earth' ? Leaf : Monitor;

  return (
    <Tooltip
      content={
        <span>
          <span className="opacity-70">Theme: </span>
          <span className="font-semibold">{LABEL[preference]}</span>
          <br />
          <span className="opacity-70">{NEXT_LABEL[preference]}</span>
        </span>
      }
      variant="rich"
      side="right"
    >
      <button
        type="button"
        onClick={cycle}
        aria-label={`Theme: ${LABEL[preference]}. ${NEXT_LABEL[preference]}.`}
        className={cn(
          'inline-flex items-center justify-center gap-1.5 rounded-md transition-colors',
          'text-ink-muted hover:text-ink hover:bg-surface-sunken',
          showLabel ? 'px-2 py-1.5 text-xs' : 'h-9 w-9',
          className
        )}
      >
        <Icon size={16} />
        {showLabel && <span>{LABEL[preference]}</span>}
      </button>
    </Tooltip>
  );
}
